import { AppLanguage } from '~/types/locale'

export interface HreflangLink {
  hreflang: string
  href: string
}

const LOCALE_ORDER: AppLanguage[] = [AppLanguage.EN, AppLanguage.NO, AppLanguage.UA]

// Default-locale (en) has no path prefix; no/uk are prefixed — mirrors useUrl.ts.
function localeHref(subdomain: string, locale: AppLanguage, basePath: string): string {
  const prefix = locale === AppLanguage.EN ? '' : `/${locale}`
  return `https://${subdomain}.houseofmath.com${prefix}${basePath}`
}

// Builds a reciprocal hreflang cluster from a per-locale path map (paths are
// locale-prefix-less; the builder adds /no|/uk). Codes are the app locale codes
// (en/no/uk) to match the sitemap. Returns [] for a single-locale item. Iterates a
// fixed locale order for deterministic output (order is SEO-irrelevant).
export function buildHreflangCluster({
  subdomain,
  pathsByLocale,
}: {
  subdomain: string
  pathsByLocale: Partial<Record<AppLanguage, string>>
}): HreflangLink[] {
  const locales = LOCALE_ORDER.filter((locale) => pathsByLocale[locale] !== undefined)
  if (locales.length < 2) return []

  const alternates = locales.map((locale) => ({
    hreflang: locale,
    href: localeHref(subdomain, locale, pathsByLocale[locale] as string),
  }))

  const xDefaultLocale = locales.includes(AppLanguage.EN) ? AppLanguage.EN : locales[0]
  return [
    ...alternates,
    { hreflang: 'x-default', href: localeHref(subdomain, xDefaultLocale, pathsByLocale[xDefaultLocale] as string) },
  ]
}

// Converts a Sanity {language, slug}[] set into a {locale -> path} map, dropping
// entries with a missing slug or an unknown language code.
export function localePathsFromSiblings(
  basePrefix: string,
  siblings: Array<{ language?: string; slug?: string }>,
): Partial<Record<AppLanguage, string>> {
  const known = new Set<string>(Object.values(AppLanguage))
  return siblings.reduce<Partial<Record<AppLanguage, string>>>((acc, { language, slug }) => {
    if (!language || !slug || !known.has(language)) return acc
    return { ...acc, [language as AppLanguage]: `${basePrefix}${slug}` }
  }, {})
}

// Decides a page's hreflang cluster. Prefer an explicit per-locale path map
// (different-slug content) when it forms a cluster (>= 2 locales); else fall back
// to same-path (encyclopedia/drill via otherLocales) — a partially-tagged
// sharedLanguageKey must not drop an existing same-path cluster. Returns [] when
// neither is usable.
export function resolveHreflangLinks({
  subdomain,
  canonicalPath,
  cleanAsPath,
  locale,
  otherLocales,
  localePaths,
}: {
  subdomain: string
  canonicalPath?: string
  cleanAsPath: string
  locale?: string
  otherLocales?: AppLanguage[]
  localePaths?: Partial<Record<AppLanguage, string>>
}): HreflangLink[] {
  if (localePaths) {
    const cluster = buildHreflangCluster({ subdomain, pathsByLocale: localePaths })
    if (cluster.length > 0) return cluster
  }
  if (locale && otherLocales && otherLocales.length > 0) {
    const basePath = (canonicalPath ?? cleanAsPath).replace(/\?.*$/g, '').replace(/\/$/, '')
    const pathsByLocale = [locale as AppLanguage, ...otherLocales].reduce<Partial<Record<AppLanguage, string>>>(
      (acc, current) => ({ ...acc, [current]: basePath }),
      {},
    )
    return buildHreflangCluster({ subdomain, pathsByLocale })
  }
  return []
}
